/* Chillab shared analytics helper.
   Single source of truth for the "publish to Shopify Analytics + dataLayer +
   GA4 Measurement Protocol" triple-report used by PDP upsell and the
   homepage SKU grid, so new call sites don't fork their own copy.
   Extracted verbatim from the implementation that shipped in
   assets/shoppable-video.js; that file now delegates here. */
window.ChillabAnalytics =
  window.ChillabAnalytics ||
  (function () {
    var GA4_ID = 'G-8Q83KJGT4E';

    function track(name, data) {
      data = data || {};
      // Keep publishing to Shopify's event bus + dataLayer for any future consumer.
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish(name, data);
        }
      } catch (e) {}
      try {
        (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data));
      } catch (e) {}
      // Send straight to GA4 from the main window. Shopify's sandboxed custom
      // pixels can't reliably reach GA, so we hit the Measurement endpoint here
      // reusing the visitor's real client + session id from the _ga cookies.
      try {
        var cm = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
        var cid = cm ? cm[1] : (Math.floor(Math.random() * 1e10) + '.' + Math.floor(Date.now() / 1000));
        var en = name.indexOf('chillab_') === 0 ? name.slice(8) : name;
        var qs =
          'v=2&tid=' + GA4_ID + '&cid=' + encodeURIComponent(cid) +
          '&_p=' + Math.floor(Math.random() * 1e9) + '&en=' + encodeURIComponent(en);
        var sm = document.cookie.match(/_ga_[A-Z0-9]+=GS\d\.\d\.(\d+)/);
        if (sm) { qs += '&sid=' + sm[1] + '&sct=1&seg=1'; }
        Object.keys(data).forEach(function (k) {
          var v = data[k];
          if (v === undefined || v === null || v === '') return;
          var pref = typeof v === 'number' ? 'epn.' : 'ep.';
          qs += '&' + pref + encodeURIComponent(k) + '=' + encodeURIComponent(v);
        });
        fetch('https://www.google-analytics.com/g/collect?' + qs, { method: 'POST', mode: 'no-cors', keepalive: true });
      } catch (e) {}
    }

    /* Klaviyo Added to Cart via klaviyo.push. This lands on an API metric,
       not Shopify-branded Vi8yAw — merchants cannot publish standard
       product_added_to_cart (Shopify.analytics.publish only emits custom
       events into customData). W5DKTR / RRma2m stay on Vi8yAw until that
       API metric is added as a trigger. Payload keys match live Vi8yAw
       templates (Product Name, ImageURL, URL, Price) plus AddedItem* aliases.
       Queue on window.klaviyo even if the app embed has not loaded yet.
       Do not call this for gift-auto-add / quantity-break free gifts. */
    function centsToAmount(cents) {
      if (typeof cents !== 'number' || !isFinite(cents)) return 0;
      return cents / 100;
    }

    function absoluteUrl(url) {
      if (!url) return '';
      if (/^https?:\/\//i.test(url)) return url;
      if (url.indexOf('//') === 0) return 'https:' + url;
      var origin = (window.location && window.location.origin) || '';
      if (url.charAt(0) !== '/') url = '/' + url;
      return origin + url;
    }

    function imageUrl(item) {
      if (!item) return '';
      if (typeof item.image === 'string' && item.image) return absoluteUrl(item.image);
      var fi = item.featured_image;
      if (!fi) return '';
      if (typeof fi === 'string') return absoluteUrl(fi);
      return absoluteUrl(fi.url || fi.src || '');
    }

    function productUrl(item) {
      if (item && item.url) return absoluteUrl(item.url);
      if (item && item.handle) {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        return absoluteUrl(root + 'products/' + item.handle);
      }
      return (window.location && window.location.href) || '';
    }

    function checkoutUrl() {
      var path = (window.routes && window.routes.cart_url) || '/cart';
      if (/^https?:\/\//i.test(path)) return path;
      return ((window.location && window.location.origin) || '') + path;
    }

    function isSystemGift(item) {
      var props = item && item.properties;
      if (!props) return false;
      return !!(props._auto_gift || props._qb_gift);
    }

    /* Shopper-facing line-item properties (engraving, gift wrap, etc.).
       Underscore-prefixed keys are system attributes and are ignored. */
    function hasCustomProperties(item) {
      var props = item && item.properties;
      if (!props) return false;
      var keys = Object.keys(props);
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].charAt(0) !== '_') return true;
      }
      return false;
    }

    /* Quantity-tier parent lines post quantity 1 with properties._chillab_tier
       = N (N >= 2). Cart Transform expands that parent into N paid units plus
       the gift set. /cart/VID:QTY permalinks cannot carry line properties, so
       a tier-2 line would be recovered as a plain single — wrong paid qty,
       no gift, and no volume discount. Empty string beats a lying permalink.
       Expanding to VID:(qty*N) is also wrong: plain qty N would let
       lineExpand mint N gifts instead of one. */
    function hasQuantityTier(item) {
      var props = item && item.properties;
      if (!props) return false;
      var raw = props._chillab_tier;
      if (raw == null || raw === '') return false;
      var n = parseInt(String(raw), 10);
      return n > 1;
    }

    /* Cross-device cart permalink from variant:qty pairs. Shopify's docs say
       a permalink ADDS to the current cart. Measured on chillab.net it
       REPLACES the whole cart. Three controlled tests on a cart holding
       variant A x1:
         open A:1  -> cart is A x1   (adding would give x2)
         open A:2  -> cart is A x2   (adding would give x3)
         open B:1  -> cart is B x1 only, A is gone
       So wrapping with /cart/clear?return_to= bought no protection against
       double quantities, while costing an extra redirect and a failure mode
       where the customer lands on an empty cart if return_to does not run.
       Emit the bare permalink. Do not encodeURIComponent: ':' and ',' are
       legal in a path segment and must stay literal here. If Shopify ever
       reverts to additive behaviour, reinstating the /cart/clear?return_to=
       wrapper is the fix. Returns '' when a permalink cannot represent
       the cart.

       Prefix with Shopify.routes.root (same as cart.js / cart/add.js). A
       bare /cart/VID:QTY from an /en-au/ session rebuilds the cart in the
       primary market -- same class of bug #383 fixed for sticky ATC, where
       bare /cart/add.js priced Max Cloudy Puff at USD $14 instead of
       AUD $20. CheckoutURL already uses the Liquid market cart path;
       CartRecoveryURL must stay in the same market or the abandoned-cart
       CTA undoes that. */
    function cartRecoveryUrl(cartItems) {
      var origin = (window.location && window.location.origin) || '';
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var pairs = [];
      if (!cartItems || !cartItems.length) return '';
      for (var i = 0; i < cartItems.length; i++) {
        var it = cartItems[i];
        if (isSystemGift(it)) continue;
        if (hasCustomProperties(it)) return '';
        if (hasQuantityTier(it)) return '';
        if (it.selling_plan_allocation) return '';
        var vid = it.variant_id != null ? it.variant_id : it.id;
        var qty = it.quantity || 1;
        if (vid == null) return '';
        pairs.push(String(vid) + ':' + qty);
      }
      if (!pairs.length) return '';
      var target = root + 'cart/' + pairs.join(',');
      if (target.length > 1500) return '';
      return origin + target;
    }

    function firstAddedItem(response) {
      if (!response || typeof response !== 'object') return null;
      if (response.status) return null;
      var bulk = response.items;
      if (
        Array.isArray(bulk) &&
        bulk.length &&
        bulk[0] &&
        (bulk[0].product_id || bulk[0].variant_id)
      ) {
        return bulk[0];
      }
      if (response.product_id || response.variant_id) return response;
      return null;
    }

    function variantName(item) {
      var title = item && item.variant_title;
      if (!title || title === 'Default Title') return '';
      return title;
    }

    function mapCartItem(item) {
      var name = item.product_title || item.title || '';
      var unit = centsToAmount(
        typeof item.final_price === 'number' ? item.final_price : item.price
      );
      var qty = item.quantity || 1;
      return {
        ProductID: item.product_id != null ? String(item.product_id) : '',
        VariantID: item.variant_id != null ? String(item.variant_id)
                 : (item.id != null ? String(item.id) : ''),
        SKU: item.sku || '',
        ProductName: name,
        Quantity: qty,
        ItemPrice: unit,
        RowTotal: unit * qty,
        ProductURL: productUrl(item),
        ImageURL: imageUrl(item),
        ProductCategories: [],
      };
    }

    function buildAddedToCartPayload(item, cart, opts) {
      opts = opts || {};
      var qty = opts.quantity > 0 ? opts.quantity : item.quantity || 1;
      var price = centsToAmount(
        typeof item.final_price === 'number' ? item.final_price : item.price
      );
      var name = item.product_title || item.title || '';
      var productId = item.product_id != null ? String(item.product_id) : '';
      var sku = item.sku || '';
      var variantId = '';
      if (item.variant_id != null) variantId = String(item.variant_id);
      else if (item.id != null) variantId = String(item.id);
      var image = imageUrl(item);
      var url = productUrl(item);
      var categories = Array.isArray(opts.categories) ? opts.categories : [];
      var currency =
        (cart && cart.currency) ||
        (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        '';
      var compareAt = null;
      if (typeof item.compare_at_price === 'number' && item.compare_at_price > 0) {
        compareAt = centsToAmount(item.compare_at_price);
      }

      var cartItems = cart && Array.isArray(cart.items) ? cart.items : [item];
      var itemNames = [];
      var items = [];
      for (var i = 0; i < cartItems.length; i++) {
        itemNames.push(cartItems[i].product_title || cartItems[i].title || '');
        items.push(mapCartItem(cartItems[i]));
      }

      return {
        $value: price * qty,
        $currency: currency,
        Price: price,
        CompareAtPrice: compareAt,
        Quantity: qty,
        'Product Name': name,
        ProductID: productId,
        'Variant SKU': sku,
        VariantID: variantId,
        'Variant Name': variantName(item),
        ImageURL: image,
        URL: url,
        Categories: categories,
        Brand: item.vendor || 'CHILLAB',
        'Product Type': item.product_type || '',
        AddedItemProductName: name,
        AddedItemProductID: productId,
        AddedItemSKU: sku,
        AddedItemCategories: categories,
        AddedItemImageURL: image,
        AddedItemURL: url,
        AddedItemPrice: price,
        AddedItemQuantity: qty,
        ItemNames: itemNames,
        CheckoutURL: checkoutUrl(),
        CartRecoveryURL: cartRecoveryUrl(cartItems),
        Items: items,
      };
    }

    function cartJsUrl() {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return root + 'cart.js';
    }

    /* Always resolves (to the cart object or null). Network errors, non-2xx,
       bad JSON, and a missing items array all become null so the Added to
       Cart event still publishes with the added line only. */
    function fetchCartJson() {
      return fetch(cartJsUrl(), { headers: { Accept: 'application/json' } }).then(
        function (res) {
          if (!res.ok) return null;
          return res.json().then(
            function (data) {
              if (!data || !Array.isArray(data.items)) return null;
              return data;
            },
            function () {
              return null;
            }
          );
        },
        function () {
          return null;
        }
      );
    }

    /* One /cart.js in flight at a time. Overlapping add-to-carts reuse it
       instead of stacking storefront requests; a later add after it settles
       fetches again. Reuse over overwrite: a double-tap is the same cart,
       and queuing a replacement behind the in-flight request would delay
       the event for no newer snapshot. Safe because publish unions a
       missing added line onto that shared snapshot (see cartWithAddedLine). */
    var cartJsInflight = null;

    function loadCartForAddedToCart() {
      if (cartJsInflight) return cartJsInflight;
      cartJsInflight = fetchCartJson().then(
        function (cart) {
          cartJsInflight = null;
          return cart;
        },
        function () {
          cartJsInflight = null;
          return null;
        }
      );
      return cartJsInflight;
    }

    /* Overlapping adds share one /cart.js, so the snapshot can resolve
       before this add is in cart.items. Union the added line onto a copy
       — do not replace the cart, or every other line disappears — so
       Items[] and CartRecoveryURL still include the product this event
       is about. Shipped cart=null always had that guarantee; reuse
       without this union silently dropped it. The appended entry keeps
       the added line's own quantity (not opts.quantity). */
    function cartWithAddedLine(cart, added) {
      if (!cart || !Array.isArray(cart.items)) return cart;
      var addedVid = added.variant_id != null ? added.variant_id : added.id;
      var i;
      for (i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        var vid = it.variant_id != null ? it.variant_id : it.id;
        if (vid == addedVid) return cart;
      }
      var merged = {};
      for (var key in cart) {
        if (Object.prototype.hasOwnProperty.call(cart, key)) merged[key] = cart[key];
      }
      var extra = {};
      for (key in added) {
        if (Object.prototype.hasOwnProperty.call(added, key)) extra[key] = added[key];
      }
      extra.quantity = added.quantity || 1;
      merged.items = cart.items.concat([extra]);
      return merged;
    }

    function trackAddedToCart(addResponse, opts) {
      window.klaviyo = window.klaviyo || [];
      var added = firstAddedItem(addResponse);
      if (!added || isSystemGift(added)) return;
      var payloadOpts = opts || {};

      function publish(cart) {
        try {
          window.klaviyo.push([
            'track',
            'Added to Cart',
            buildAddedToCartPayload(added, cartWithAddedLine(cart, added), payloadOpts),
          ]);
        } catch (e) {}
      }

      loadCartForAddedToCart().then(publish, function () {
        publish(null);
      });
    }

    /* One event per checkout entry point so PDP accelerated checkout can be
       told apart from the drawer and /cart buttons. Both currently land in
       Shopify Payments as Shop Pay / Apple Pay, so wallet share alone does
       not say which button was tapped. Capture-phase delegated click: the
       drawer re-renders its footer on every cart change, and Shopify
       replaces the inner accelerated-checkout node. Never preventDefault
       or await — track() already uses keepalive, and a throw must not
       swallow the navigation. */
    var CHECKOUT_START_EVENT = 'chillab_checkout_start';
    var checkoutStartBound = false;

    function toInt(value) {
      var n = parseInt(value, 10);
      return isFinite(n) ? n : undefined;
    }

    function activeCurrency() {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) {
        return Shopify.currency.active;
      }
      return undefined;
    }

    function headerCartItemCount() {
      var el = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (!el) return undefined;
      return toInt((el.textContent || '').replace(/[^\d]/g, ''));
    }

    function quantitySum(root) {
      if (!root || !root.querySelectorAll) return undefined;
      var inputs = root.querySelectorAll('.quantity__input');
      if (!inputs.length) return undefined;
      var total = 0;
      var i;
      for (i = 0; i < inputs.length; i++) {
        var q = parseInt(inputs[i].value, 10);
        if (isFinite(q) && q > 0) total += q;
      }
      return total;
    }

    /* Cart-path `value` must be a live presentment-currency subtotal.
       `.free-shipping-bar[data-subtotal-cents]` is not that: it is the
       first-paint figure, and on data-needs-convert markets it is USD
       cents (see assets/cart-recommendations.js). Qty changes already
       publish cartData.items_subtotal_price (assets/cart.js); keep the
       latest copy. If a cartUpdate does not carry that field — add.js
       is a line item, some publishers omit cartData — drop the snapshot
       rather than report a stale number. No /cart.js on the click path. */
    var lastCartSubtotalCents;

    function rememberCartSubtotal(event) {
      try {
        var cents = event && event.cartData && event.cartData.items_subtotal_price;
        if (typeof cents === 'number' && isFinite(cents) && cents >= 0) {
          lastCartSubtotalCents = cents;
        } else {
          lastCartSubtotalCents = undefined;
        }
      } catch (e) {
        lastCartSubtotalCents = undefined;
      }
    }

    function liveCartSubtotalAmount() {
      if (typeof lastCartSubtotalCents !== 'number' || !isFinite(lastCartSubtotalCents)) {
        return undefined;
      }
      return centsToAmount(lastCartSubtotalCents);
    }

    function nativeProductForm(productForm) {
      if (!productForm) return null;
      return productForm.querySelector('form') || productForm;
    }

    function productFormQuantity(productForm) {
      var nativeForm = nativeProductForm(productForm);
      if (!nativeForm) return 1;
      var input = nativeForm.querySelector('input[name="quantity"]');
      if (!input && nativeForm.id) {
        input = document.querySelector('input[name="quantity"][form="' + nativeForm.id + '"]');
      }
      if (!input) return 1;
      var q = parseInt(input.value, 10);
      return q > 0 ? q : 1;
    }

    function productFormIdInput(productForm) {
      if (!productForm) return null;
      return productForm.querySelector('.product-variant-id') ||
        productForm.querySelector('input[name="id"]');
    }

    function productFormVariantId(productForm) {
      var input = productFormIdInput(productForm);
      var id = input ? toInt(input.value) : undefined;
      return id > 0 ? id : undefined;
    }

    function productFormProductId(productForm) {
      var input = productFormIdInput(productForm);
      var fromInput = input ? toInt(input.getAttribute('data-productid')) : undefined;
      if (fromInput > 0) return fromInput;
      if (productForm) {
        var sectionId = productForm.getAttribute('data-section-id') || '';
        var pdpset = sectionId.match(/^pdpset-.+-(\d+)$/);
        var fromSection = pdpset ? toInt(pdpset[1]) : undefined;
        if (fromSection > 0) return fromSection;
        var host = productForm.closest('[data-product-id]');
        var fromHost = host ? toInt(host.getAttribute('data-product-id')) : undefined;
        if (fromHost > 0) return fromHost;
      }
      return undefined;
    }

    function selectedVariantPriceCents(productForm, variantId) {
      if (!productForm || variantId == null) return undefined;
      var scope = productForm.closest('product-info') || productForm;
      var script = scope.querySelector('[data-selected-variant]');
      if (!script || !script.textContent) return undefined;
      try {
        var variant = JSON.parse(script.textContent);
        if (!variant || typeof variant.price !== 'number') return undefined;
        if (variant.id != null && Number(variant.id) !== Number(variantId)) return undefined;
        return variant.price;
      } catch (e) {
        return undefined;
      }
    }

    function metaVariantPriceCents(variantId) {
      if (variantId == null) return undefined;
      var analytics = window.ShopifyAnalytics;
      var meta = analytics && analytics.meta && analytics.meta.product;
      if (!meta || !meta.variants || !meta.variants.length) return undefined;
      var i;
      for (i = 0; i < meta.variants.length; i++) {
        var v = meta.variants[i];
        if (!v || Number(v.id) !== Number(variantId)) continue;
        if (typeof v.price === 'number') return v.price;
        var parsed = toInt(v.price);
        if (parsed != null) return parsed;
      }
      return undefined;
    }

    function pdpLineValue(productForm, variantId, quantity) {
      var cents = selectedVariantPriceCents(productForm, variantId);
      if (typeof cents !== 'number') cents = metaVariantPriceCents(variantId);
      if (typeof cents !== 'number') return undefined;
      return centsToAmount(cents) * quantity;
    }

    /* Apple Pay is a third of orders and only renders in Safari; do not
       assume it sits in .shopify-payment-button. Tag suffix covers
       shop-pay-wallet-button and other wallet hosts. closest() only —
       no extra listeners. */
    function closestWalletButton(target) {
      var node = target.nodeType === 1 ? target : target.parentElement;
      while (node && node.nodeType === 1) {
        var tag = node.tagName;
        if (tag && tag.toLowerCase().slice(-14) === '-wallet-button') return node;
        node = node.parentElement;
      }
      return null;
    }

    function isAcceleratedCheckoutTarget(target) {
      if (target.closest('.shopify-payment-button')) return true;
      if (target.closest('shopify-accelerated-checkout')) return true;
      if (target.closest('apple-pay-button')) return true;
      return !!closestWalletButton(target);
    }

    /* A page that merely contains .product-form__submit (featured-product
       on the homepage, a collection, etc.) must not label a wallet click
       elsewhere as pdp_express. Featured-product itself uses the same
       product-form / product-info wrap as the PDP, so exclude it. */
    function isPdpExpressHost(target) {
      if (target.closest('.featured-product, .section-featured-product')) return false;
      return !!(
        target.closest('product-form') ||
        target.closest('product-info') ||
        target.closest('.product__info-container')
      );
    }

    function resolveCheckoutPath(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return '';
      var checkoutBtn = target.closest('button[name="checkout"].cart__checkout-button');
      if (checkoutBtn) {
        return target.closest('cart-drawer') ? 'cart_drawer' : 'cart_page';
      }
      if (isAcceleratedCheckoutTarget(target) && isPdpExpressHost(target)) {
        return 'pdp_express';
      }
      return '';
    }

    function checkoutStartPayload(path, event) {
      var data = { path: path };
      var currency = activeCurrency();
      if (currency) data.currency = currency;

      if (path === 'pdp_express') {
        var productForm = event.target.closest('product-form');
        var qty = productFormQuantity(productForm);
        data.item_count = qty;
        var variantId = productFormVariantId(productForm);
        var productId = productFormProductId(productForm);
        if (variantId != null) data.variant_id = variantId;
        if (productId != null) data.product_id = productId;
        var lineValue = pdpLineValue(productForm, variantId, qty);
        if (typeof lineValue === 'number') data.value = lineValue;
        return data;
      }

      var root = null;
      if (path === 'cart_drawer') {
        root = event.target.closest('cart-drawer');
      } else {
        root = document.getElementById('main-cart-items') || document.querySelector('cart-items');
      }

      var count = quantitySum(root);
      if (typeof count !== 'number') count = headerCartItemCount();
      if (typeof count === 'number') data.item_count = count;

      var value = liveCartSubtotalAmount();
      if (typeof value === 'number') data.value = value;
      return data;
    }

    /* Persist cart_drawer / cart_page on the cart as `_checkout_entry` so
       the value lands on the order. Leading underscore hides it from the
       buyer and from the order confirmation. GA4 chillab_checkout_start
       is lossy: ad blockers drop it, Shop Pay overlays break session
       attribution, and some office networks block the beacon.

       pdp_express is deliberately excluded. Accelerated checkout bypasses
       the cart — Shopify builds checkout from the product form — so there
       is no cart to attach an attribute to at click time. Writing here
       would either be lost or, worse, attach to a different future order
       built from leftover cart contents. A silently wrong number is worse
       than a missing one; pdp_express stays GA4-only.

       sendBeacon is queued by the browser and survives page teardown. A
       normal fetch to cart/update.js races the checkout navigation and
       is often cancelled. If sendBeacon is missing, skip the write; do
       not fall back to a blocking request.

       Use routes.cart_update_url (market-prefixed by Liquid), same as
       cart.js. A bare /cart/update.js from /en-au/ (or any subfolder
       market) runs in the primary-market Ajax context — the same class
       as sticky ATC #383 and CartRecoveryURL #421, where a bare cart
       endpoint flipped Max Cloudy Puff from AUD $20 to USD $14. This
       write fires on every cart-drawer / cart-page checkout click, so
       a market switch here races the hand-off into checkout.

       Known limits: the beacon can lose the race on a very fast checkout
       hand-off, so the attribute is a lower bound, not a census. It
       cannot cover pdp_express at all. `_checkout_entry` and GA4 will
       therefore disagree; that is expected — they measure different
       things. Do not add reconciliation logic. */
    function writeCheckoutEntryAttribute(path) {
      if (path !== 'cart_drawer' && path !== 'cart_page') return;
      if (!navigator.sendBeacon) return;
      var url =
        (window.routes && window.routes.cart_update_url) ||
        (((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/update.js');
      var blob = new Blob(
        [JSON.stringify({ attributes: { _checkout_entry: path } })],
        { type: 'application/json' }
      );
      navigator.sendBeacon(url, blob);
    }

    function onCheckoutStartClick(event) {
      try {
        var path = resolveCheckoutPath(event);
        if (!path) return;
        track(CHECKOUT_START_EVENT, checkoutStartPayload(path, event));
        writeCheckoutEntryAttribute(path);
      } catch (e) {}
    }

    function bindCheckoutStartTracking() {
      if (checkoutStartBound) return;
      checkoutStartBound = true;
      document.addEventListener('click', onCheckoutStartClick, true);
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, rememberCartSubtotal);
        }
      } catch (e) {}
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bindCheckoutStartTracking);
    } else {
      bindCheckoutStartTracking();
    }

    return {
      track: track,
      trackAddedToCart: trackAddedToCart,
      buildAddedToCartPayload: buildAddedToCartPayload,
      cartRecoveryUrl: cartRecoveryUrl,
      GA4_ID: GA4_ID,
    };
  })();
